import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import BrandLogo from "@/components/BrandLogos";
import ProofUpload from "@/components/ProofUpload";
import ImageLightbox from "@/components/ImageLightbox";
import {
  CORRIDORS, DEPOSIT_METHODS, RECEPTION_METHODS,
  formatAmount, formatDate,
} from "@/lib/constants";
import { toast } from "sonner";
import {
  ShieldCheck, KeyRound, Lock, ArrowRight,
  FileImage, Loader2, AlertTriangle, CheckCircle2,
  XCircle, FileText,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Agent-only transaction viewer.
 * URL: /agent/voir?code=SL-XXXXXXXX
 * The agent must enter their 6-digit personal access code (issued by admin) to unlock the recap.
 */
export default function AgentView() {
  const [params] = useSearchParams();
  const txCode = (params.get("code") || "").toUpperCase().trim();

  const [accessCode, setAccessCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null); // { agent_name, transaction }
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  // Keep the validated access_code so we can reuse it for follow-up actions
  const [unlockedCode, setUnlockedCode] = useState("");
  const [completing, setCompleting] = useState(false);
  const [kycDeciding, setKycDeciding] = useState(null); // "accept" | "reject" | null
  const [payoutProofPath, setPayoutProofPath] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!txCode) {
      setError("Aucune référence de transaction fournie dans le lien.");
      return;
    }
    const cleaned = accessCode.replace(/\D/g, "");
    if (cleaned.length !== 6) {
      setError("Le code agent doit contenir 6 chiffres.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/agent-view/access`, {
        tx_code: txCode,
        access_code: cleaned,
      });
      setData(res);
      setUnlockedCode(cleaned);
      toast.success(`Accès autorisé — bienvenue ${res.agent_name}`);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Code incorrect.";
      setError(msg);
      setData(null);
    } finally {
      setSubmitting(false);
    }
  };

  const completeTransaction = async () => {
    if (!data || !unlockedCode) return;
    const tx = data.transaction;
    if (!payoutProofPath) {
      toast.error("Veuillez d'abord téléverser la capture du paiement au bénéficiaire.");
      return;
    }
    const ok = window.confirm(
      `Confirmer : la transaction ${tx.code} a bien été remise au bénéficiaire et peut être marquée comme effectuée ?`
    );
    if (!ok) return;
    setCompleting(true);
    try {
      const { data: res } = await api.post(`/agent-view/complete`, {
        tx_code: tx.code,
        access_code: unlockedCode,
        payout_proof_path: payoutProofPath,
      });
      setData({ ...data, transaction: res.transaction });
      toast.success("Transaction marquée comme effectuée ✓");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  const decideKyc = async (decision, reason) => {
    if (!data || !unlockedCode) return;
    const tx = data.transaction;
    setKycDeciding(decision);
    try {
      const body = { tx_code: tx.code, access_code: unlockedCode, decision };
      if (decision === "reject") body.reason = reason || "Pièce d'identité non conforme";
      const { data: res } = await api.post(`/agent-view/kyc-decision`, body);
      setData({ ...data, transaction: res.transaction });
      toast.success(
        decision === "accept"
          ? "Pièce d'identité acceptée ✓"
          : "Pièce refusée — transaction annulée"
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setKycDeciding(null);
    }
  };

  return (
    <div className="min-h-screen sl-hero-bg">
      <div className="pt-10 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="flex items-center gap-3 mb-8">
            <span
              className="w-11 h-11 rounded-full grid place-items-center text-white"
              style={{ background: "var(--sl-primary-dark)" }}
            >
              <ShieldCheck size={20} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">
                Espace agent
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold">Vérification du dépôt</h1>
            </div>
          </header>

          {!data ? (
            <form onSubmit={submit} className="sl-card" data-testid="agent-view-gate">
              <div className="flex items-start gap-3 mb-6">
                <span className="w-10 h-10 rounded-full grid place-items-center bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] flex-shrink-0">
                  <Lock size={18} />
                </span>
                <div className="flex-1">
                  <h2 className="font-heading text-lg font-semibold">Accès réservé aux agents</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Saisissez votre <span className="font-semibold">code d'accès personnel à 6 chiffres</span> pour consulter les détails et la capture du dépôt.
                  </p>
                  {txCode && (
                    <div className="mt-3 text-xs text-gray-500">
                      Référence : <span className="font-mono font-bold text-gray-700">{txCode}</span>
                    </div>
                  )}
                </div>
              </div>

              <label className="sl-label flex items-center gap-2">
                <KeyRound size={14} /> Votre code agent
              </label>
              <input
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="• • • • • •"
                className="sl-input font-mono text-center text-2xl tracking-[0.6em]"
                data-testid="agent-access-input"
              />
              {error && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-start gap-2" data-testid="agent-access-error">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || accessCode.length !== 6 || !txCode}
                className="sl-btn-primary mt-5 w-full justify-center"
                data-testid="agent-access-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {submitting ? "Vérification…" : "Déverrouiller la transaction"}
              </button>

              <p className="text-xs text-gray-500 mt-4">
                Vous n'avez pas de code ? Contactez l'administrateur Swiftlink-Africa.
                Après 5 tentatives erronées, l'accès est bloqué pendant 5 minutes.
              </p>
            </form>
          ) : (
            <Recap
              data={data}
              onComplete={completeTransaction}
              completing={completing}
              onKycDecision={decideKyc}
              kycDeciding={kycDeciding}
              payoutProofPath={payoutProofPath}
              setPayoutProofPath={setPayoutProofPath}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Recap({ data, onComplete, completing, onKycDecision, kycDeciding, payoutProofPath, setPayoutProofPath }) {
  const tx = data.transaction;
  const c = CORRIDORS[tx.corridor || "tg_ga"];
  const proofUrl = tx.proof_path ? `${API_BASE}/transactions/${tx.code}/proof` : "";
  const isFinalized = tx.status === "termine" || tx.status === "annule";
  const kycToken = data.kyc_view_token;
  const kycUrl = tx.kyc_doc_path && kycToken
    ? `${API_BASE}/agent-view/${tx.code}/kyc-doc?token=${encodeURIComponent(kycToken)}`
    : "";
  const kycStatus = tx.kyc_status || (tx.kyc_doc_path ? "pending" : null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const docTypeLabel = {
    cni: "Carte d'identité (CNI)",
    passeport: "Passeport",
    permis: "Permis de conduire",
  }[tx.kyc_doc_type] || tx.kyc_doc_type || "—";

  return (
    <div className="sl-card" data-testid="agent-view-recap">
      <div
        className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2 mb-5"
        data-testid="agent-view-banner"
      >
        <ShieldCheck size={16} />
        <span>
          Connecté en tant que <span className="font-semibold">{data.agent_name}</span> · accès autorisé
        </span>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500">Référence</div>
          <div className="font-mono text-2xl font-bold" data-testid="agent-view-tx-code">{tx.code}</div>
          <div className="text-xs text-gray-500 mt-1">
            {c?.flagFrom} {c?.label} {c?.flagTo}
          </div>
        </div>
        <StatusBadge status={tx.status} cancellationReason={tx.cancellation_reason} />
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4 text-center">
        <Box label="Vous recevez (au dépôt)" value={formatAmount(tx.amount_xof, tx.source_currency || "XOF")} />
        <Box label="Frais Swiftlink-Africa" value={formatAmount(tx.fee, tx.source_currency || "XOF")} accent />
        <Box
          label="À envoyer au bénéficiaire"
          value={formatAmount(tx.amount_xaf, tx.target_currency || "XAF")}
          primary
        />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6 text-sm">
        <div>
          <h4 className="font-heading font-semibold mb-2">Expéditeur</h4>
          <div className="text-gray-700">{tx.sender_name}</div>
          <div className="text-gray-500">{tx.sender_phone}</div>
          {tx.sender_email && <div className="text-gray-500">{tx.sender_email}</div>}
          <div className="mt-3 flex items-center gap-2 text-gray-500">
            <BrandLogo method={tx.deposit_method} size={28} />
            <span>
              Dépôt :{" "}
              <span className="font-medium text-gray-700">
                {DEPOSIT_METHODS[tx.deposit_method]?.label}
              </span>
            </span>
          </div>
          {tx.payment_reference && (
            <div className="text-gray-500 mt-1">
              Réf. paiement : <span className="font-mono text-gray-700">{tx.payment_reference}</span>
            </div>
          )}
        </div>
        <div>
          <h4 className="font-heading font-semibold mb-2">Bénéficiaire</h4>
          <div className="text-gray-700">{tx.beneficiary_name}</div>
          {tx.beneficiary_phone && <div className="text-gray-500">{tx.beneficiary_phone}</div>}
          {tx.beneficiary_email && <div className="text-gray-500">{tx.beneficiary_email}</div>}
          {tx.beneficiary_account && (
            <div className="text-gray-500 font-mono text-xs break-all">{tx.beneficiary_account}</div>
          )}
          <div className="mt-3 flex items-center gap-2 text-gray-500">
            <BrandLogo method={tx.reception_method} size={28} />
            <span>
              Réception :{" "}
              <span className="font-medium text-gray-700">
                {RECEPTION_METHODS[tx.reception_method]?.label}
              </span>
            </span>
          </div>
        </div>
      </div>

      {kycStatus && (
        <div className="mt-6 rounded-xl border-2 p-5"
          style={{
            borderColor:
              kycStatus === "verified" ? "rgb(16 185 129)" :
              kycStatus === "rejected" ? "rgb(220 38 38)" :
              "rgb(245 158 11)",
            background:
              kycStatus === "verified" ? "rgb(236 253 245)" :
              kycStatus === "rejected" ? "rgb(254 242 242)" :
              "rgb(255 251 235)",
          }}
          data-testid="kyc-review-card"
        >
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <ShieldCheck size={18} className={
              kycStatus === "verified" ? "text-emerald-700" :
              kycStatus === "rejected" ? "text-red-700" :
              "text-amber-700"
            } />
            Vérification d'identité (KYC)
            {kycStatus === "verified" && (
              <span className="ml-auto text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 size={14} /> Pièce acceptée
              </span>
            )}
            {kycStatus === "rejected" && (
              <span className="ml-auto text-xs font-semibold text-red-700 inline-flex items-center gap-1">
                <XCircle size={14} /> Pièce refusée
              </span>
            )}
            {kycStatus === "pending" && (
              <span className="ml-auto text-xs font-semibold text-amber-700">À vérifier</span>
            )}
          </h3>

          <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
            <Field label="Type de pièce" value={docTypeLabel} />
            <Field label="Nom complet" value={tx.kyc_full_name || "—"} />
            <Field label="Numéro de pièce" value={tx.kyc_doc_number || "—"} mono />
          </div>

          {kycUrl ? (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setLightbox({ src: kycUrl, alt: "Pièce d'identité" })}
                className="block group w-full text-left"
                data-testid="kyc-doc-open"
              >
                {/* If the file is a PDF, the inline <img> will fail; we offer "Ouvrir" toolbar inside Lightbox. */}
                <div className="relative">
                  <img
                    src={kycUrl}
                    alt="Pièce d'identité"
                    className="max-h-[420px] rounded-lg border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
                    style={{ borderColor: "var(--sl-border)" }}
                    data-testid="kyc-doc-image"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="mt-2 text-xs text-gray-500 text-center inline-flex items-center justify-center w-full gap-1">
                    <FileText size={12} /> Cliquez pour agrandir la pièce (PDF : utilisez « Ouvrir »)
                  </div>
                </div>
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-500 mb-4">Aucune pièce KYC pour cette transaction.</div>
          )}

          {kycStatus === "rejected" && tx.kyc_rejection_reason && (
            <div className="mt-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800" data-testid="kyc-reject-reason-shown">
              <span className="font-semibold">Motif du refus :</span> {tx.kyc_rejection_reason}
            </div>
          )}
          {tx.kyc_reviewed_by?.agent_name && (
            <div className="text-xs text-gray-500 mt-2">
              Décision par <span className="font-semibold">{tx.kyc_reviewed_by.agent_name}</span>{" "}
              · {tx.kyc_reviewed_at ? formatDate(tx.kyc_reviewed_at) : ""}
            </div>
          )}

          {kycStatus === "pending" && (
            <div className="mt-4 pt-4 border-t border-amber-200">
              {!rejectMode ? (
                <div className="flex flex-wrap gap-3 items-center" data-testid="kyc-decision-actions">
                  <button
                    onClick={() => onKycDecision("accept")}
                    disabled={!!kycDeciding}
                    className="sl-btn-primary"
                    style={{ background: "rgb(5 150 105)" }}
                    data-testid="kyc-accept-btn"
                  >
                    {kycDeciding === "accept" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    {kycDeciding === "accept" ? "Validation…" : "Pièce conforme — accepter"}
                  </button>
                  <button
                    onClick={() => setRejectMode(true)}
                    disabled={!!kycDeciding}
                    className="sl-btn-outline"
                    style={{ borderColor: "rgb(220 38 38)", color: "rgb(185 28 28)" }}
                    data-testid="kyc-reject-btn"
                  >
                    <XCircle size={16} /> Refuser la pièce
                  </button>
                  <span className="text-xs text-gray-600 ml-1">
                    Refuser annule la transaction immédiatement.
                  </span>
                </div>
              ) : (
                <div className="space-y-3" data-testid="kyc-reject-form">
                  <label className="sl-label">Motif du refus (visible par le client)</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="sl-input"
                    placeholder="Ex : photo floue, document expiré, nom illisible…"
                    data-testid="kyc-reject-reason"
                  />
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => onKycDecision("reject", rejectReason)}
                      disabled={!rejectReason.trim() || !!kycDeciding}
                      className="sl-btn-primary"
                      style={{ background: "#B91C1C" }}
                      data-testid="kyc-confirm-reject-btn"
                    >
                      <XCircle size={16} />
                      {kycDeciding === "reject" ? "Envoi…" : "Confirmer le refus"}
                    </button>
                    <button
                      onClick={() => { setRejectMode(false); setRejectReason(""); }}
                      className="sl-btn-outline"
                      disabled={!!kycDeciding}
                      data-testid="kyc-cancel-reject-btn"
                    >
                      Retour
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {proofUrl && (
        <div className="mt-6" data-testid="agent-view-proof-block">
          <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileImage size={16} className="text-[var(--sl-primary)]" /> Capture du dépôt
          </h4>
          <button
            type="button"
            onClick={() => setLightbox({ src: proofUrl, alt: "Capture du dépôt" })}
            className="block group w-full text-left"
            data-testid="agent-view-proof-open"
          >
            <img
              src={proofUrl}
              alt="Capture du dépôt"
              className="max-h-[420px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
              style={{ borderColor: "var(--sl-border)" }}
              data-testid="agent-view-proof-image"
            />
            <div className="mt-2 text-xs text-gray-500 text-center">
              Cliquez pour agrandir
            </div>
          </button>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500">
        Reçue {formatDate(tx.created_at)}
      </div>

      {/* Payout proof — required before agent can mark as completed */}
      {!isFinalized && (
        <div className="mt-6 pt-6 border-t border-gray-100" data-testid="payout-proof-block">
          <h4 className="font-heading font-semibold mb-2 flex items-center gap-2">
            <FileImage size={16} className="text-[var(--sl-primary)]" />
            Capture du paiement au bénéficiaire <span className="text-red-500">*</span>
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Téléversez la capture d'écran de votre virement / transfert effectué au bénéficiaire.
            Elle restera attachée à la transaction pour traçabilité.
          </p>
          <ProofUpload
            value={payoutProofPath}
            onChange={setPayoutProofPath}
          />
        </div>
      )}

      {/* Show existing payout proof when already completed */}
      {isFinalized && tx.status === "termine" && tx.payout_proof_path && (
        <div className="mt-6 pt-6 border-t border-gray-100" data-testid="payout-proof-saved">
          <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileImage size={16} className="text-emerald-700" /> Preuve du paiement bénéficiaire
          </h4>
          <button
            type="button"
            onClick={() => setLightbox({
              src: `${API_BASE}/transactions/${tx.code}/payout-proof`,
              alt: "Preuve du paiement bénéficiaire",
            })}
            className="block group w-full text-left"
            data-testid="payout-proof-saved-open"
          >
            <img
              src={`${API_BASE}/transactions/${tx.code}/payout-proof`}
              alt="Preuve de paiement"
              className="max-h-[360px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
              style={{ borderColor: "var(--sl-border)" }}
            />
            <div className="mt-2 text-xs text-gray-500 text-center">Cliquez pour agrandir</div>
          </button>
          {tx.payout_proof_uploaded_by?.agent_name && (
            <div className="text-xs text-gray-500 mt-2 text-center">
              Téléversée par <span className="font-semibold">{tx.payout_proof_uploaded_by.agent_name}</span>{" "}
              · {tx.payout_proof_uploaded_at ? formatDate(tx.payout_proof_uploaded_at) : ""}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-gray-100 flex flex-wrap gap-3 items-center">
        {!isFinalized && (
          <button
            onClick={onComplete}
            disabled={completing || !payoutProofPath}
            className="sl-btn-primary"
            data-testid="agent-view-complete-btn"
            title={!payoutProofPath ? "Téléversez d'abord la capture du paiement" : ""}
          >
            {completing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {completing ? "Validation…" : "Marquer comme effectuée"}
          </button>
        )}
        {isFinalized && tx.status === "termine" && (
          <div
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800"
            data-testid="agent-view-already-completed"
          >
            <CheckCircle2 size={16} /> Transaction déjà marquée comme effectuée.
          </div>
        )}
        <Link
          to={`/suivi?code=${tx.code}`}
          className="sl-btn-outline"
          data-testid="agent-view-public-link"
        >
          Voir la page de suivi publique <ArrowRight size={14} />
        </Link>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`Bonjour, votre transfert Swiftlink-Africa ${tx.code} a bien été remis au bénéficiaire. Merci pour votre confiance.`)}`}
          target="_blank"
          rel="noreferrer"
          className="sl-btn-outline"
          data-testid="agent-view-whatsapp-client"
        >
          Notifier le client par WhatsApp
        </a>
      </div>
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          downloadHref={lightbox.src}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function Box({ label, value, primary, accent }) {
  const cls = primary ? "text-[var(--sl-primary)]" : accent ? "text-amber-700" : "text-gray-900";
  return (
    <div className="rounded-xl bg-[var(--sl-bg-alt)] p-4">
      <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading text-lg font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className={`text-gray-800 ${mono ? "font-mono text-sm" : ""}`}>{value || "—"}</div>
    </div>
  );
}
